/**
 * "Clientes deste login" no diálogo de edição de um login de portal (spec 034).
 * O portal de um login é exatamente o conjunto destes vínculos. Vale na hora
 * (não espera o "Salvar" do diálogo) e mostra isso.
 *
 * - adicionar: o MESMO seletor de cliente com busca no servidor dos diálogos
 *   do ticket (ClientPicker, GET /clients?search=), só deste tenant;
 * - tirar: o último não sai — o botão fica desligado e a API responde 409
 *   `last_client_link` (sem cliente o portal fica vazio).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import type { UserClientLink } from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/misc';
import { ClientPicker, type ClientRef } from '@/features/tickets/ClientPicker';
import { capitalize, useTerms } from '@/shared/tenant-profile';
import { loginClientsQuery } from './user-clients-queries';
import { userClientsErrorMessage } from './user-clients';

export function LoginClientsSection({ userId }: { userId: number }) {
  const terms = useTerms();
  const queryClient = useQueryClient();
  const linksQ = useQuery(loginClientsQuery(userId));
  // o seletor só aparece quando o admin pede: a lista de clientes ao lado dos
  // vínculos confundia "o que está vinculado" com "o que dá para vincular"
  const [adding, setAdding] = useState(false);
  const [picking, setPicking] = useState<ClientRef | null>(null);
  const [error, setError] = useState<string | null>(null);

  const done = (rows: UserClientLink[]) => {
    queryClient.setQueryData(loginClientsQuery(userId).queryKey, rows);
    // a coluna "Cliente" da lista
    void queryClient.invalidateQueries({ queryKey: ['erp', 'users', 'client-links'] });
    setError(null);
  };
  const fail = (err: unknown) => {
    const code = (err as { error?: string } | null)?.error ?? '';
    setError(userClientsErrorMessage(code));
  };

  const add = useMutation({
    mutationFn: async (clientId: number) => {
      const res = await api<UserClientLink[]>(`/users/${userId}/clients`, {
        method: 'POST',
        body: { clientId },
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (rows) => {
      done(rows);
      setPicking(null);
      setAdding(false);
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: async (clientId: number) => {
      const res = await api<UserClientLink[]>(`/users/${userId}/clients/${clientId}`, {
        method: 'DELETE',
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: done,
    onError: fail,
  });

  const links = linksQ.data ?? [];
  const busy = add.isPending || remove.isPending;
  const only = links.length === 1;

  return (
    <div className="space-y-2" data-testid="spec034-clientes-do-login">
      <div className="flex items-baseline justify-between gap-2">
        <Label>{capitalize(terms.clients)} deste login</Label>
        <span className="text-xs text-muted-foreground">Vale na hora</span>
      </div>
      {linksQ.isPending ? (
        <Skeleton className="h-9 w-full" />
      ) : linksQ.isError ? (
        <p className="text-sm text-destructive">Erro ao carregar os vínculos.</p>
      ) : links.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nenhum vínculo — o portal deste login está vazio.
        </p>
      ) : (
        <ul className="divide-y rounded-md border">
          {links.map((l) => (
            <li key={l.clientId} className="flex items-center justify-between gap-2 px-3 py-1.5">
              <span className="min-w-0 truncate text-sm">{l.clientName}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                className="text-muted-foreground"
                disabled={busy || only}
                title={
                  only
                    ? 'É o único vínculo: sem ele o portal fica vazio'
                    : `Desvincular ${l.clientName}`
                }
                aria-label={`Desvincular ${l.clientName}`}
                onClick={() => remove.mutate(l.clientId)}
              >
                <X className="size-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}
      {adding ? (
        <ClientPicker
          selected={picking}
          onSelect={(c) => {
            setPicking(c);
            setError(null);
          }}
        />
      ) : (
        <Button type="button" variant="outline" size="sm" onClick={() => setAdding(true)}>
          <Plus className="size-4" />
          Vincular
        </Button>
      )}
      {adding ? (
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              setAdding(false);
              setPicking(null);
            }}
          >
            Fechar busca
          </Button>
          {picking ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy || links.some((l) => l.clientId === picking.id)}
              onClick={() => add.mutate(picking.id)}
            >
              {links.some((l) => l.clientId === picking.id)
                ? 'Já vinculado'
                : add.isPending
                  ? 'Vinculando…'
                  : `Vincular ${picking.name}`}
            </Button>
          ) : null}
        </div>
      ) : null}
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
    </div>
  );
}
